import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, Scales, Sun, Moon, Warning, ArrowUp, ArrowDown, Funnel, Check } from "phosphor-react";
import { format } from "date-fns";
import GeniLoader from "../components/GeniLoader";

export default function HandSizeTradesDetail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filterType = searchParams.get('type') || 'all'; // oversized, undersized, material, all
  const setupFilter = searchParams.get('setup') || '';

  const [loading, setLoading] = useState(true);
  const [trades, setTrades] = useState([]);
  const [setups, setSetups] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  // Sorting state
  const [sortColumn, setSortColumn] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tradesRes, setupsRes, analyticsRes] = await Promise.all([
        api.get('/trades'),
        api.get('/setups'),
        api.get('/analytics')
      ]);
      setTrades(tradesRes.data);
      setSetups(setupsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Helper: Calculate median
  const calculateMedian = (arr) => {
    if (arr.length === 0) return 0;
    const sorted = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0 
      ? sorted[mid] 
      : (sorted[mid - 1] + sorted[mid]) / 2;
  };

  // Helper: Get trade type
  const getTradeType = (timeframe) => {
    if (!timeframe) return 'day';
    const tf = timeframe.toUpperCase();
    const swingTimeframes = ['4H', '6H', '8H', '12H', '1D', 'D', '1W', 'W'];
    return swingTimeframes.some(swTf => tf.includes(swTf)) ? 'swing' : 'day';
  };

  // Get setup name by ID
  const getSetupName = (setupId) => {
    if (!setupId) return '—';
    const setup = setups.find(s => s.id === setupId);
    return setup?.name || '—';
  };

  // Process and filter trades
  const processedTrades = useMemo(() => {
    // Start with closed trades that have valid hand_size
    let eligibleTrades = trades.filter(t => 
      t.status === 'closed' && 
      t.hand_size !== null && 
      t.hand_size !== undefined &&
      t.hand_size > 0
    );

    // Apply setup filter if provided
    if (setupFilter) {
      if (setupFilter === '__no_setup__') {
        eligibleTrades = eligibleTrades.filter(t => !t.setup_id);
      } else {
        eligibleTrades = eligibleTrades.filter(t => t.setup_id === setupFilter);
      }
    }

    if (eligibleTrades.length < 5) {
      return { insufficientData: true, trades: [], baselineHandSize: 0 };
    }

    // Calculate baseline (median)
    const handSizes = eligibleTrades.map(t => t.hand_size);
    const baselineHandSize = calculateMedian(handSizes);

    // Consistency band (±15%)
    const CONSISTENCY_THRESHOLD = 0.15;
    const lowerBand = baselineHandSize * (1 - CONSISTENCY_THRESHOLD);
    const upperBand = baselineHandSize * (1 + CONSISTENCY_THRESHOLD);

    // Material threshold (1% of initial capital)
    const initialCapital = analytics?.initial_capital || 10000;
    const materialThreshold = initialCapital * 0.01;

    // Classify each trade
    const classifiedTrades = eligibleTrades.map(t => {
      const handSize = t.hand_size;
      const deviation = handSize - baselineHandSize;
      const deviationPercent = (deviation / baselineHandSize) * 100;
      
      let classification;
      if (handSize < lowerBand) {
        classification = 'undersized';
      } else if (handSize > upperBand) {
        classification = 'oversized';
      } else {
        classification = 'consistent';
      }

      const isMaterialAlert = classification !== 'consistent' && Math.abs(deviation) > materialThreshold;

      return {
        ...t,
        _hsClassification: classification,
        _hsDeviation: deviation,
        _hsDeviationPercent: deviationPercent,
        _isMaterialAlert: isMaterialAlert,
        _tradeType: getTradeType(t.timeframe),
        _baselineHandSize: baselineHandSize
      };
    });

    // Filter by type
    let filteredTrades = classifiedTrades;
    if (filterType === 'oversized') {
      filteredTrades = classifiedTrades.filter(t => t._hsClassification === 'oversized');
    } else if (filterType === 'undersized') {
      filteredTrades = classifiedTrades.filter(t => t._hsClassification === 'undersized');
    } else if (filterType === 'material') {
      filteredTrades = classifiedTrades.filter(t => t._isMaterialAlert);
    }

    return {
      insufficientData: false,
      trades: filteredTrades,
      baselineHandSize,
      lowerBand,
      upperBand,
      materialThreshold,
      totalClassified: classifiedTrades.length
    };
  }, [trades, setupFilter, filterType, analytics]);

  // Sorted trades
  const sortedTrades = useMemo(() => {
    if (processedTrades.insufficientData) return [];

    let sorted = [...processedTrades.trades];

    sorted.sort((a, b) => {
      let aVal, bVal;

      switch (sortColumn) {
        case 'date':
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
          break;
        case 'hand_size':
          aVal = a.hand_size || 0;
          bVal = b.hand_size || 0;
          break;
        case 'deviation':
          aVal = Math.abs(a._hsDeviationPercent) || 0;
          bVal = Math.abs(b._hsDeviationPercent) || 0;
          break;
        default:
          return 0;
      }

      return sortDirection === 'desc' ? bVal - aVal : aVal - bVal;
    });

    return sorted;
  }, [processedTrades, sortColumn, sortDirection]);

  // Handle column sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Get filter type label
  const getFilterTypeLabel = () => {
    switch (filterType) {
      case 'oversized': return 'Oversized Trades';
      case 'undersized': return 'Undersized Trades';
      case 'material': return 'Material Sizing Alerts';
      default: return 'All Trades';
    }
  };

  // Get classification badge
  const getClassificationBadge = (classification, isMaterial) => {
    if (classification === 'consistent') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded bg-[#00E676]/10 text-[#00E676] border border-[#00E676]/20">
          <Check size={10} weight="bold" />
          Consistent
        </span>
      );
    }
    if (classification === 'oversized') {
      return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded ${
          isMaterial ? 'bg-[#FF3B30]/20 border-[#FF3B30]/40' : 'bg-[#FF3B30]/10 border-[#FF3B30]/20'
        } text-[#FF3B30] border`}>
          <ArrowUp size={10} weight="bold" />
          Over {isMaterial && '(!)'}
        </span>
      );
    }
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded ${
        isMaterial ? 'bg-[#FFA726]/20 border-[#FFA726]/40' : 'bg-[#FFA726]/10 border-[#FFA726]/20'
      } text-[#FFA726] border`}>
        <ArrowDown size={10} weight="bold" />
        Under {isMaterial && '(!)'}
      </span>
    );
  };

  // Get trade type badge
  const getTradeTypeBadge = (tradeType) => {
    if (tradeType === 'swing') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded bg-purple-500/10 text-purple-400 border border-purple-500/20">
          <Moon size={10} weight="fill" />
          Swing
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">
        <Sun size={10} weight="fill" />
        Day
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <GeniLoader />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/advanced')}
            className="p-2 rounded-sm bg-white/5 hover:bg-white/10 transition-colors"
            data-testid="back-to-advanced"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-3">
            <Scales size={28} weight="duotone" className="text-purple-400" />
            <div>
              <h1 className="text-2xl font-bold font-heading">{getFilterTypeLabel()}</h1>
              <p className="text-white/40 text-sm">
                Hand Size Consistency Analysis
                {setupFilter && ` · ${setups.find(s => s.id === setupFilter)?.name || 'No setup'}`}
              </p>
            </div>
          </div>
        </div>

        {processedTrades.insufficientData ? (
          <div className="bg-[#121212] border border-white/10 rounded-sm p-12 text-center">
            <Scales size={48} weight="duotone" className="text-white/20 mx-auto mb-4" />
            <p className="text-white/40 font-semibold">Not enough sizing data</p>
            <p className="text-white/30 text-sm mt-1">
              Need at least 5 closed trades with valid hand size for analysis.
            </p>
          </div>
        ) : (
          <>
            {/* Summary Stats */}
            <div className="bg-[#121212] border border-white/10 rounded-sm p-4 mb-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-6">
                  <div>
                    <div className="text-white/40 text-xs font-mono">SHOWING</div>
                    <div className="text-xl font-bold font-mono text-white">{sortedTrades.length}</div>
                  </div>
                  <div className="h-8 w-px bg-white/10" />
                  <div>
                    <div className="text-white/40 text-xs font-mono">BASELINE</div>
                    <div className="text-xl font-bold font-mono text-purple-400">${processedTrades.baselineHandSize?.toFixed(2)}</div>
                  </div>
                  <div className="h-8 w-px bg-white/10" />
                  <div>
                    <div className="text-white/40 text-xs font-mono">BAND</div>
                    <div className="text-sm font-mono text-white/60">
                      ${processedTrades.lowerBand?.toFixed(2)} - ${processedTrades.upperBand?.toFixed(2)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <Funnel size={14} className="text-white/40" />
                  <span className="text-white/40">Filter:</span>
                  <span className={`px-2 py-0.5 rounded ${
                    filterType === 'oversized' ? 'bg-[#FF3B30]/20 text-[#FF3B30]' :
                    filterType === 'undersized' ? 'bg-[#FFA726]/20 text-[#FFA726]' :
                    filterType === 'material' ? 'bg-purple-500/20 text-purple-400' :
                    'bg-white/10 text-white/60'
                  }`}>
                    {getFilterTypeLabel()}
                  </span>
                </div>
              </div>
            </div>

            {/* Trades Table */}
            <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
              <table className="w-full" data-testid="hand-size-trades-table">
                <thead className="bg-[#0A0A0A]">
                  <tr className="text-left text-xs text-white/40 font-mono">
                    <th className="p-3">ASSET</th>
                    <th className="p-3">TYPE</th>
                    <th className="p-3">SETUP</th>
                    <th 
                      className="p-3 cursor-pointer hover:text-white transition-colors"
                      onClick={() => handleSort('hand_size')}
                    >
                      HAND SIZE {sortColumn === 'hand_size' && (sortDirection === 'desc' ? '↓' : '↑')}
                    </th>
                    <th 
                      className="p-3 cursor-pointer hover:text-white transition-colors"
                      onClick={() => handleSort('deviation')}
                    >
                      DEVIATION {sortColumn === 'deviation' && (sortDirection === 'desc' ? '↓' : '↑')}
                    </th>
                    <th className="p-3">CLASSIFICATION</th>
                    <th 
                      className="p-3 cursor-pointer hover:text-white transition-colors"
                      onClick={() => handleSort('date')}
                    >
                      DATE {sortColumn === 'date' && (sortDirection === 'desc' ? '↓' : '↑')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTrades.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-white/40">
                        No trades match the current filter
                      </td>
                    </tr>
                  ) : (
                    sortedTrades.map((trade) => (
                      <tr
                        key={trade.id}
                        className="border-t border-white/10 hover:bg-white/5 transition-colors"
                        data-testid={`trade-row-${trade.id}`}
                      >
                        <td className="p-3">
                          <div className="font-mono text-white font-bold">{trade.asset}</div>
                          <div className="text-xs text-white/40">{trade.timeframe || '-'}</div>
                        </td>
                        <td className="p-3">
                          {getTradeTypeBadge(trade._tradeType)}
                        </td>
                        <td className="p-3">
                          <span className="text-white/60 text-sm">{getSetupName(trade.setup_id)}</span>
                        </td>
                        <td className="p-3">
                          <span className="font-mono text-white font-semibold">
                            ${trade.hand_size?.toFixed(2)}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className={`font-mono text-sm ${
                            trade._hsDeviationPercent > 0 ? 'text-[#FF3B30]' :
                            trade._hsDeviationPercent < 0 ? 'text-[#FFA726]' :
                            'text-white/60'
                          }`}>
                            {trade._hsDeviationPercent >= 0 ? '+' : ''}{trade._hsDeviationPercent?.toFixed(1)}%
                            <span className="text-white/30 text-xs ml-1">
                              (${trade._hsDeviation >= 0 ? '+' : ''}{trade._hsDeviation?.toFixed(2)})
                            </span>
                          </span>
                        </td>
                        <td className="p-3">
                          {getClassificationBadge(trade._hsClassification, trade._isMaterialAlert)}
                        </td>
                        <td className="p-3">
                          <span className="text-white/60 text-sm">
                            {trade.created_at && format(new Date(trade.created_at), "MMM d, yyyy")}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {/* Results count */}
              <div className="p-3 border-t border-white/10 text-xs text-white/40">
                Showing {sortedTrades.length} of {processedTrades.totalClassified} classified trades
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
